import { useEffect, useState } from "react";
import { Button, Card, Typography, Divider, Form, Select, Flex, Checkbox, InputNumber } from "antd";
import { LeftOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { changeLoader } from "../../../redux/reducers/loader";
import { useDispatch } from "react-redux";
import globalRequest from "../../../utils/global/globalRequest";
import * as Apis from "../../../utils/helper/enum";
import toast from "react-hot-toast";
import { selectTime } from "../../../utils/helper/constants";
import * as Validation from "../../../utils/helper/validation";
import { formatDateFunction } from "../../../utils/helper/utils";

const daysName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const EditDriverHours = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  let { id } = useParams();

  const initialTimeSlot = {
    openTime: null,
    closeTime: null,
    total_trip_distance: null,
    openTimeErr: null,
    closeTimeErr: null,
    total_trip_distanceErr: null,
  };
  const [timeSlots, setTimeSlots] = useState({
    1: [], // Sunday
    2: [], // Monday
    3: [], // Tuesday
    4: [], // Wednesday
    5: [], // Thursday
    6: [], // Friday
    7: [], // Saturday
  });

  const [edit, setEdit] = useState(false);
  const [holidays, setHolidays] = useState([]);

  const handleAddSlot = (day) => {
    const newSlots = { ...timeSlots };
    newSlots[day].push({ ...initialTimeSlot });
    setTimeSlots(newSlots);
  };

  const handleRemoveSlot = (day, index) => {
    const newSlots = { ...timeSlots };
    if (newSlots[day].length > 1) {
      newSlots[day].splice(index, 1);
      setTimeSlots(newSlots);
    }
  };

  const handleTimeChange = (day, index, field, value) => {
    if (field === "isChecked") {
      const updatedSlots = timeSlots[day].map((slot, slotIndex) => {
        if (slotIndex === index) {
          return { ...slot, [field]: value };
        }
        return slot;
      });
      setTimeSlots({
        ...timeSlots,
        [day]: updatedSlots,
      });
    } else {
      const updatedSlots = timeSlots[day].map((slot, slotIndex) => {
        if (slotIndex === index) {
          return { ...slot, [field]: value, [`${field}Err`]: null };
        }
        return slot;
      });
      setTimeSlots({
        ...timeSlots,
        [day]: updatedSlots,
      });
    }
  };

  const validateSlots = () => {
    let hasError = false;
    const newSlots = { ...timeSlots };

    // Function to check if two time slots overlap
    const isOverlapping = (slot1, slot2) => {
      return slot1.openTime < slot2.closeTime && slot2.openTime < slot1.closeTime;
    };

    Object.keys(newSlots).forEach((day) => {
      const daySlots = newSlots[day];

      // Validate each slot
      daySlots.forEach((slot, index) => {
        if (!slot.openTime) {
          newSlots[day][index].openTimeErr = "Open time is required.";
          hasError = true;
        }
        if (!slot.closeTime) {
          newSlots[day][index].closeTimeErr = "Close time is required.";
          hasError = true;
        }
        // Validate that closeTime is greater than openTime
        if (slot.total_trip_distance === undefined || slot.total_trip_distance === null || slot.total_trip_distance === "") {
          newSlots[day][index].total_trip_distanceErr = "Total trip distance is required.";
          hasError = true;
        } else if (!Number.isInteger(Number(slot.total_trip_distance))) {
          newSlots[day][index].total_trip_distanceErr = "Only integer values are allowed.";
          hasError = true;
        } else if (slot.total_trip_distance < 0) {
          newSlots[day][index].total_trip_distanceErr = "Invalid distance.";
          hasError = true;
        } else if (slot.total_trip_distance.toString().length > 9) {
          newSlots[day][index].total_trip_distanceErr = "Total trip distance should not exceed 8 digits.";
          hasError = true;
        }
      });

      // Check for overlapping slots for the same day
      for (let i = 0; i < daySlots.length; i++) {
        for (let j = i + 1; j < daySlots.length; j++) {
          if (isOverlapping(daySlots[i], daySlots[j])) {
            newSlots[day][i].openTimeErr = "Time slots must not overlap.";
            newSlots[day][j].openTimeErr = "Time slots must not overlap.";
            hasError = true;
          }
        }
      }
    });

    setTimeSlots(newSlots);
    return !hasError;
  };

  const handleSubmit = async () => {
    if (!validateSlots()) return;

    let time_slots = [];

    for (let i in timeSlots) {
      if (timeSlots[i].length > 0) {
        let slots = [];
        for (let j in timeSlots[i]) {
          slots.push({
            id: timeSlots[i][j].id || 0,
            openTime: timeSlots[i][j].openTime,
            closeTime: timeSlots[i][j].closeTime,
            total_trip_distance: Number(timeSlots[i][j].total_trip_distance),
          });
        }
        time_slots.push({
          day: daysName[i - 1].toLowerCase(),
          slots: slots,
        });
      }
    }

    let insertArray = {
      driverId: id,
      time_slots: time_slots,
    };
    dispatch(changeLoader(true));
    try {
      let url = Apis?.DRIVER_TIME_SLOTE?.CREATE_TIME_SLOTE;
      let method = "post";
      if (edit) {
        url = Apis?.DRIVER_TIME_SLOTE?.EDIT_TIME_SLOTE;
        method = "put";
      }
      let response = await globalRequest(url, method, insertArray, {}, true);
      let apiResponse = response?.data;
      if (response?.status === 200) {
        let msg = "Working hours added successfully";
        if (edit) msg = "Working hours updated successfully";
        toast.success(msg, {
          duration: 2000,
        });
        localStorage.setItem("currentTab", "3");
        navigate("/edit-driver/" + id);
      } else {
        toast.error(apiResponse?.message);
      }
    } catch (e) {
      toast.error(e?.message);
    }
    dispatch(changeLoader(false));
  };

  const getTimeSlotes = async (driverId) => {
    let params = {
      id: driverId,
      type: "all",
      slotFor: "driver",
    };
    try {
      let response = await globalRequest(Apis.DRIVER_TIME_SLOTE.GET_ALL_TIME_SLOTE, "get", {}, { params: params }, true);
      let apiResponse = response?.data;
      if (response?.status === 200) {
        setHolidays(apiResponse?.data?.holidays || []);
        let slotsList = apiResponse?.data?.data || apiResponse?.data || [];
        if (slotsList && !Array.isArray(slotsList) && Array.isArray(slotsList.data)) {
          slotsList = slotsList.data;
        }
        
        if (!slotsList || slotsList.length === 0) {
          setEdit(false);
        } else {
          setEdit(true);
          let data = slotsList;
          console.log("getTimeSlotes data:", data);

          let sunday = [];
          let monday = [];
          let tuesday = [];
          let wednesday = [];
          let thursday = [];
          let friday = [];
          let saturday = [];
          for (let i in data) {
            const slotItem = {
              id: data[i].id || data[i]._id || 0,
              openTime: data[i].openTime || data[i].startTime,
              closeTime: data[i].closeTime || data[i].endTime,
              openTimeErr: null,
              closeTimeErr: null,
              total_trip_distance: data[i].total_trip_distance !== undefined && data[i].total_trip_distance !== null 
                ? data[i].total_trip_distance 
                : (data[i].tripDistance !== undefined && data[i].tripDistance !== null 
                  ? data[i].tripDistance 
                  : (data[i].capacity !== undefined && data[i].capacity !== null ? data[i].capacity : "")),
              total_trip_distanceErr: null,
            };
            const itemDay = data[i].day?.toLowerCase();
            if (itemDay === "sunday") sunday.push(slotItem);
            if (itemDay === "monday") monday.push(slotItem);
            if (itemDay === "tuesday") tuesday.push(slotItem);
            if (itemDay === "wednesday") wednesday.push(slotItem);
            if (itemDay === "thursday") thursday.push(slotItem);
            if (itemDay === "friday") friday.push(slotItem);
            if (itemDay === "saturday") saturday.push(slotItem);
          }

          setTimeSlots({
            1: sunday,
            2: monday,
            3: tuesday,
            4: wednesday,
            5: thursday,
            6: friday,
            7: saturday,
          });
        }
      } else {
        toast.error(apiResponse?.message);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getTimeSlotes(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCancel = () => {
    localStorage.setItem("currentTab", "3");
    navigate("/edit-driver/" + id);
  };

  return (
    <>
      <Card>
        <div className="">
          <Typography className="text-xl font-semibold" align={"left"}>
            <LeftOutlined className="mr-2" onClick={handleCancel} />
            Add Working hours
          </Typography>
        </div>
        <Divider />
        <div className="min-h-[calc(100vh_-_305px)]">
          {holidays && holidays.length > 0 && (
            <div className="mb-5 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h4 className="text-orange-800 font-semibold mb-2 text-base">Active Holidays:</h4>
              <div className="flex flex-wrap gap-2">
                {holidays.map((h, idx) => (
                  <span key={idx} className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                    {h.startDate === h.endDate
                      ? formatDateFunction(h.startDate, "dd/mm/yyyy")
                      : `${formatDateFunction(h.startDate, "dd/mm/yyyy")} to ${formatDateFunction(h.endDate, "dd/mm/yyyy")}`}
                    {h.description ? ` (${h.description})` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          <Form
            layout="vertical"
            initialValues={{
              remember: true,
            }}
            autoComplete="off"
          >
            {Object.keys(timeSlots).map((day) => (
              <div key={day}>
                <Flex gap="middle" className="w-full ">
                  <div className="grid grid-cols-4 items-start">
                    <Checkbox
                      checked={timeSlots[day]?.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTimeSlots((prev) => ({
                            ...prev,
                            [day]: [{ ...initialTimeSlot }],
                          }));
                        } else {
                          setTimeSlots((prev) => ({
                            ...prev,
                            [day]: [],
                          }));
                        }
                      }}
                    >
                      {daysName[day - 1]}
                    </Checkbox>
                    <div className="flex flex-col gap-4">
                      {timeSlots[day]?.map((slot, index) => (
                        <div key={index} className="flex gap-4 ">
                          <div className="w-full">
                            <Select
                              size="large"
                              placeholder="Select"
                              options={selectTime}
                              showSearch
                              filterOption={(input, selectTime) => (selectTime?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                              onChange={(value) => {
                                handleTimeChange(day, index, "openTime", value);
                              }}
                              value={slot.openTime}
                              className="w-[200px]"
                            />
                            {slot.openTimeErr && <span className="text-red-100">{slot.openTimeErr}</span>}
                          </div>
                          <div className="w-full">
                            <Select
                              size="large"
                              placeholder="Select"
                              options={selectTime}
                              showSearch
                              filterOption={(input, selectTime) => (selectTime?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                              onChange={(value) => handleTimeChange(day, index, "closeTime", value)}
                              value={slot.closeTime}
                              className="w-[200px]"
                            />
                            {slot.closeTimeErr && <span className="text-red-100">{slot.closeTimeErr}</span>}
                          </div>
                          <div className="w-full">
                            <InputNumber
                              type="text"
                              size="large"
                              placeholder="Total Trip Distance"
                              className="w-[200px]"
                              maxLength={8}
                              onChange={(value) => {
                                let val = Validation.strFormat(value, "number");
                                handleTimeChange(day, index, "total_trip_distance", val);
                              }}
                              value={slot.total_trip_distance}
                              addonAfter="Km"
                            />
                            {slot.total_trip_distanceErr && <span className="text-red-100">{slot.total_trip_distanceErr}</span>}
                          </div>
                          {index === timeSlots[day]?.length - 1 ? (
                            <div className="w-full">
                              <Button icon={<PlusOutlined />} onClick={() => handleAddSlot(day)} />
                            </div>
                          ) : (
                            <div className="w-full">
                              <Button icon={<MinusOutlined />} onClick={() => handleRemoveSlot(day, index)} danger />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </Flex>
                <Divider className="mt-5" />
              </div>
            ))}
          </Form>
        </div>
        <Divider />
        <div className="flex justify-end gap-3">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" onClick={() => handleSubmit()}>
            Save
          </Button>
        </div>
      </Card>
    </>
  );
};

export default EditDriverHours;
